import { isWeb } from '@gluestack-ui/nativewind-utils/IsWeb';
import { type ReactNode } from 'react';

import { AnnouncementBanner } from '@/components/AnnouncementBanner';
import { GlobalBannerErrorBoundary } from '@/components/GlobalBannerErrorBoundary';
import { useAnnouncementSurface } from '@/hooks/useAnnouncements';

/**
 * Builds the native announcement banner for a screen to render directly under its
 * header, or `undefined` when there is nothing to show.
 *
 * Native screens render their own announcement, where web renders one shared card
 * from `GlobalBannerSlot`. Three screens used to spread the same eleven props into
 * `AnnouncementBanner` by hand, so every new config field meant editing four call
 * sites; this holds that wiring once.
 *
 * This is a hook returning a node rather than a component, and it must stay one.
 * A screen wraps the node to fit its own padding and tests it for presence, so a
 * component that returns `null` would still read as a banner and leave an empty
 * wrapper behind. Only an absent node is absent.
 *
 * `buttonClassName="px-5"` widens the CTA's own padding past the size variant's
 * default, matching the web card. It is not a gutter alignment, despite the banner
 * running full-bleed under the header.
 */
export function useAnnouncementHeaderBanner(): ReactNode | undefined {
  const { config, handleCtaPress, isAnnouncementReady, announcementSignature } =
    useAnnouncementSurface();

  if (isWeb || !isAnnouncementReady) {
    return undefined;
  }

  return (
    <GlobalBannerErrorBoundary surface="announcement-header-banner">
      <AnnouncementBanner
        isEnabled
        contentKey={announcementSignature}
        variant={config?.variant}
        title={config?.title}
        message={config?.message}
        emoji={config?.emoji}
        showIcon={config?.showIcon}
        ctaLabel={config?.ctaLabel}
        ctaUrl={config?.ctaUrl}
        ctaAction={config?.ctaAction}
        onPress={handleCtaPress}
        buttonClassName="px-5"
      />
    </GlobalBannerErrorBoundary>
  );
}
